/**
 * Editor edits kept when a sign-in expired before they were saved
 * (ZLAYT-1645, reviews of MR 958 rounds 5 and 6).
 *
 * The text is client data, so it is kept as little as possible:
 * sessionStorage (this tab only; it survives the same-tab redirect to the
 * sign-in page and goes when the tab closes), keyed by the user and the
 * document, for two hours at most, and removed by a successful save of
 * that document, by Restore or Discard, and by every deliberate or forced
 * sign-out (AuthProvider.logout, ApiHandler's inactive account path).
 */
import { getStoredAccessToken } from './refreshAccessToken';

export const UNSENT_DRAFT_TTL_MS = 2 * 60 * 60 * 1000;
const PREFIX = 'rteUnsentDraft:';

export interface UnsentDraft {
  html: string;
  /** The server's copy the edit was made on, to detect a later change. */
  serverHtml: string;
  keptAt: number;
}

/** simplejwt's user_id claim of the stored access token, or null. */
export function signedInUserId(): string | null {
  const token = getStoredAccessToken();
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const id = JSON.parse(atob(padded))?.user_id;
    return id === undefined || id === null || id === '' ? null : String(id);
  } catch {
    return null;
  }
}

const keyFor = (userId: string, documentKey: string) =>
  `${PREFIX}${userId}:${documentKey}`;

export function keepUnsentDraft(
  documentKey: string,
  html: string,
  serverHtml: string,
  now: number = Date.now()
): void {
  const userId = signedInUserId();
  if (!userId || !html) return;
  try {
    sessionStorage.setItem(
      keyFor(userId, documentKey),
      JSON.stringify({ html, serverHtml, keptAt: now })
    );
  } catch {
    // Storage blocked or full: nothing more can be done before sign-out.
  }
}

export function readUnsentDraft(
  documentKey: string,
  now: number = Date.now()
): UnsentDraft | null {
  const userId = signedInUserId();
  if (!userId) return null;
  const key = keyFor(userId, documentKey);
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    const fresh =
      typeof draft?.keptAt === 'number' &&
      now - draft.keptAt <= UNSENT_DRAFT_TTL_MS;
    if (!fresh || typeof draft.html !== 'string' || !draft.html) {
      sessionStorage.removeItem(key);
      return null;
    }
    return {
      html: draft.html,
      serverHtml: typeof draft.serverHtml === 'string' ? draft.serverHtml : '',
      keptAt: draft.keptAt,
    };
  } catch {
    return null;
  }
}

export function clearUnsentDraft(documentKey: string): void {
  const userId = signedInUserId();
  if (!userId) return;
  try {
    sessionStorage.removeItem(keyFor(userId, documentKey));
  } catch {
    // Nothing to clear.
  }
}

/** Every kept edit of every user in this tab. */
export function clearAllUnsentDrafts(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(PREFIX)) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Nothing to clear.
  }
}
